import axios from 'axios';

const axiosInstance = axios.create({
    baseURL: '/rapidcraft-service/api/rapidcraft',
});

/**
 * Clears all authentication-related data from browser storage.
 * Used when receiving 401/403 responses to force re-authentication.
 */
const clearAuthData = () => {
    localStorage.removeItem('isUserLoggedIn');
    localStorage.removeItem('idToken');
    localStorage.removeItem('ads.loggedInUser');
    sessionStorage.removeItem('ads.loggedInUser');
};

/**
 * Request interceptor: Attaches the Bearer token to all outgoing requests
 */
axiosInstance.interceptors.request.use(
    (config) => {
        // Retrieve the token from a secure source (e.g., local storage)
        const token = localStorage.getItem('idToken');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        // Don't set Content-Type for FormData - let browser set it with boundary
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Response interceptor: Handles authentication errors globally.
 * When a 401 (Unauthorized) or 403 (Forbidden) response is received:
 * 1. Clears all stored authentication data
 * 2. Redirects user to the login page
 *
 * This prevents users from being stuck with stale/invalid tokens.
 */
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status;

        // Handle authentication errors
        if (status === 401 || status === 403) {
            console.warn(`Authentication error (${status}): Clearing auth data and redirecting to login`);

            // Clear all auth data
            clearAuthData();

            // Only redirect if not already on login page to prevent redirect loops
            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login';
            }
        }

        return Promise.reject(error);
    }
);

export const axiosIamInstance = axios.create({
    baseURL: '/rapidcraft-service/api/iamv2',
});

// Apply the same interceptors to axiosIamInstance for consistency
axiosIamInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('idToken');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        // Don't set Content-Type for FormData - let browser set it with boundary
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

axiosIamInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status;

        if (status === 401 || status === 403) {
            console.warn(`Authentication error (${status}): Clearing auth data and redirecting to login`);
            clearAuthData();

            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login';
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
